import React, {
    useCallback,
    useEffect,
    useState,
    startTransition,
    useMemo,
    useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import connect from '@app/utils/connect';
import {
    AI_INSIGHTS,
    VALUATION_INSIGHTS,
    FILTER_REDESIGN,
} from '@app/constants/config';
import HomeTabs from './HomeTabs';
import { HOME_TABS } from './tabs';
import Overview from './overview';
import ValuationInsights from './valuation-insights/ValuationInsights';
import homeActions from '../actions/home';
import accountTypes from './accountTypes';
import AutomatedInsights from '../components/automated-insights';
import { Col, Flex, Typography, Row } from '@app/ui';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useHomeTabContext } from '@app/home/HomeTabContext';
import { useRightsholderAttribution } from '@app/components/hooks/useRightsholderAttribution';

type HomeProps = {
    client: any;
    home: any;
    filters: any;
    user: any;
    getMVByAccType: (type: string) => string;
    getCardsTotal: () => void;
    getTopPlatformTotal: () => void;
    getTopPlatformGraph: (granularity: string) => void;
    getTopPlacementTotal: () => void;
    getTopPlacementGraph: (granularity: string) => void;
};

const Home = ({ client, home, filters, user, ...homeActions }: HomeProps) => {
    const { t } = useTranslation(['home', 'common']);
    const [changesIds, setChangesIDs] = useState({
        cards: null,
        mvByAccType: {
            [accountTypes.mediaType]: null,
            [accountTypes.social]: null,
            [accountTypes.broadcast]: null,
        },
        topPlatform: {
            total: null,
            graph: null,
        },
        topPlacement: {
            total: null,
            graph: null,
        },
    });
    const [accTypeSelected, setAccTypeSelected] = useState(
        accountTypes.mediaType,
    );
    const hasAiInsightsPagePermissions =
        client.permissions.includes(AI_INSIGHTS);

    const hasValuationInsightsPagePermissions =
        client.permissions.includes(VALUATION_INSIGHTS);

    const canAccessFilterRedesign =
        client.permissions.includes(FILTER_REDESIGN);

    // Get tab state from context (provided by AppContainer)
    const { selectedTab: contextTab, setSelectedTab } = useHomeTabContext();

    // Get rightsholder attribution state
    const { rightsholderAttribution, hasPermission: showRhAttribution } =
        useRightsholderAttribution({ client });
    const prevRightsholderAttributionRef = useRef(rightsholderAttribution);

    // Use local state that syncs with context
    const [selectedTab, setSelectedTabLocal] = useState(
        contextTab || HOME_TABS.OVERVIEW.name,
    );

    // Sync local state with context when context changes
    React.useEffect(() => {
        if (contextTab) {
            setSelectedTabLocal(contextTab);
        }
    }, [contextTab]);

    // Update context when local tab changes
    const handleTabChange = React.useCallback(
        (tab: string) => {
            setSelectedTabLocal(tab);
            setSelectedTab(tab);
        },
        [setSelectedTab],
    );

    useEffect(() => {
        syncTotalCardsData();
        syncMVByAccTypeData();
        syncTopPlatformsData();
        syncTopPlacementsData();
    }, [filters.changesId]);

    useEffect(() => {
        syncMVByAccTypeData();
    }, [accTypeSelected]);

    // Refetch data when rightsholder attribution toggle changes
    useEffect(() => {
        if (
            showRhAttribution &&
            rightsholderAttribution !== undefined &&
            prevRightsholderAttributionRef.current !==
                rightsholderAttribution &&
            prevRightsholderAttributionRef.current !== undefined
        ) {
            // Don't fetch immediately with stale filters from previous mode.
            // AppContainer triggers getFilters() on attribution change; once
            // filters.changesId updates, sync*Data effects will refetch safely.
            // Reset changesIds to force refetch with new toggle value
            setChangesIDs({
                cards: null,
                mvByAccType: {
                    [accountTypes.mediaType]: null,
                    [accountTypes.social]: null,
                    [accountTypes.broadcast]: null,
                },
                topPlatform: {
                    total: null,
                    graph: null,
                },
                topPlacement: {
                    total: null,
                    graph: null,
                },
            });
        }
        // Update ref after checking
        prevRightsholderAttributionRef.current = rightsholderAttribution;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rightsholderAttribution, showRhAttribution, accTypeSelected]);

    const syncTotalCardsData = () => {
        if (changesIds.cards !== filters.changesId) {
            startTransition(() => {
                homeActions.getCardsTotal();
                setChangesIDs({ ...changesIds, cards: filters.changesId });
            });
        }
    };

    const selectAccType = useCallback(type => {
        startTransition(() => {
            setAccTypeSelected(type);
        });
    }, []);

    const syncTopPlatformsData = async (newGranularity = null) => {
        if (filters.isLoading) return;

        const { graph } = home.topPlatform;
        const { granularity } = graph;

        const newChangesIds = { ...changesIds };
        if (changesIds.topPlatform.total !== filters.changesId) {
            await homeActions.getTopPlatformTotal();
            newChangesIds.topPlatform.total = filters.changesId;
        }

        if (
            changesIds.topPlatform.graph !== filters.changesId ||
            newGranularity !== granularity
        ) {
            homeActions.getTopPlatformGraph(newGranularity || granularity);
            newChangesIds.topPlatform.graph = filters.changesId;
            startTransition(() => {
                setChangesIDs(newChangesIds);
            });
        }
    };

    const syncTopPlacementsData = async (newGranularity = null) => {
        if (filters.isLoading) return;

        const { graph } = home.topPlacement;
        const { granularity } = graph;

        const newChangeIds = { ...changesIds };
        if (changesIds.topPlacement.total !== filters.changesId) {
            await homeActions.getTopPlacementTotal();
            newChangeIds.topPlacement.total = filters.changesId;
        }

        if (
            changesIds.topPlacement.graph !== filters.changesId ||
            newGranularity !== granularity
        ) {
            homeActions.getTopPlacementGraph(newGranularity || granularity);
            newChangeIds.topPlacement.graph = filters.changesId;
            startTransition(() => {
                setChangesIDs(newChangeIds);
            });
        }
    };

    const syncMVByAccTypeData = () => {
        if (changesIds.mvByAccType[accTypeSelected] !== filters.changesId) {
            homeActions.getMVByAccType(accTypeSelected);
            const newChangesIds = { ...changesIds };
            newChangesIds.mvByAccType[accTypeSelected] = filters.changesId;
            startTransition(() => {
                setChangesIDs(newChangesIds);
            });
        }
    };

    const showTabs =
        hasValuationInsightsPagePermissions || hasAiInsightsPagePermissions;

    const selectedTabName = useMemo(() => {
        const tab = Object.values(HOME_TABS).find(
            tab => tab.name === selectedTab,
        );
        return tab ? t(tab.titleKey) : '';
    }, [selectedTab, t]);

    return (
        <Flex
            dir="column"
            style={{
                flexDirection: 'column',
                padding: 24,
            }}
        >
            {showTabs && (
                <>
                    <Flex
                        justify="flex-start"
                        align="center"
                        gap={32}
                        style={{
                            height: '72px',
                            marginBottom: '24px',
                        }}
                    >
                        <img
                            src={client.headerImageUrl}
                            alt={t('clientLogo', { clientName: client.name })}
                            style={{
                                height: '72px',
                                width: 'auto',
                                objectFit: 'contain',
                                maxWidth: '200px',
                            }}
                        />
                        <Typography.Text
                            className="-ellipsis"
                            style={{
                                margin: 0,
                                fontSize: 30,
                                fontWeight: 500,
                            }}
                        >
                            {client.name}
                        </Typography.Text>
                    </Flex>

                    <HomeTabs
                        onTabSelection={handleTabChange}
                        selectedTab={selectedTab}
                    />

                    {selectedTab === HOME_TABS.AI_INSIGHTS.name &&
                        showRhAttribution &&
                        rightsholderAttribution === 'league' && (
                            <Flex
                                justify="flex-end"
                                align="center"
                                gap={8}
                                style={{
                                    marginTop: '16px',
                                    marginBottom: '-8px',
                                }}
                            >
                                <InfoCircleOutlined
                                    style={{
                                        color: '#1890ff',
                                        fontSize: '14px',
                                    }}
                                />
                                <Typography.Text
                                    style={{
                                        fontSize: '14px',
                                        color: '#666',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {t(
                                        'rightsholderAttribution.automatedInsightsInfo',
                                        { ns: 'common' },
                                    )}
                                </Typography.Text>
                            </Flex>
                        )}

                    <Typography.Title
                        level={4}
                        style={{
                            color: '#1f1f1f',
                            fontWeight: 400,
                            marginTop: '24px',
                        }}
                    >
                        {selectedTabName}
                    </Typography.Title>
                </>
            )}
            <Row gutter={16}>
                {selectedTab === HOME_TABS.AI_INSIGHTS.name &&
                    !user.ssoLogin && (
                        <Col span={24}>
                            <AutomatedInsights />
                        </Col>
                    )}
                {selectedTab === HOME_TABS.OVERVIEW.name && (
                    <Col span={24}>
                        <Overview
                            home={home}
                            client={client}
                            rightsholders={filters.rightsholders}
                            selectAccType={selectAccType}
                            accTypeSelected={accTypeSelected}
                            syncTopPlatformsData={syncTopPlatformsData}
                            syncTopPlacementsData={syncTopPlacementsData}
                            canAccessFilterRedesign={canAccessFilterRedesign}
                        />
                    </Col>
                )}

                {selectedTab === HOME_TABS.VALUATION_INSIGHTS.name && (
                    <Col span={24}>
                        <ValuationInsights />
                    </Col>
                )}
            </Row>
        </Flex>
    );
};

export default connect(
    ['client', 'home', 'filters', 'user'],
    homeActions,
)(Home);
